import React from 'react';
import PropTypes from 'prop-types';
import { graphlib, layout } from 'ciena-dagre/lib';
import { DropTarget } from 'react-dnd';
import { Alert } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { buildFlowInputsMap } from 'components/pages/flows/utils';
import FlowGraphNode from './FlowGraphNode';
import FlowGraphStartNode from './FlowGraphStartNode';
import FlowGraphTriggerNode from './FlowGraphTriggerNode';
import FlowGraphEdge from './FlowGraphEdge';
import {
	generateNodeFromNodeHandler
} from './FlowGraphUtils';
import FlowPropTypes from '../PropTypes';

const START_NODE_ID = '$$__axway__start';
const TRIGGER_ID_PREFIX = '$$__axway__trigger_';
const ENDPOINT_ID_PREFIX = '$$__axway__endpoint_';
const TOP = '$$__axway__top';
const DRAG_PAN_SPEED = 5;

const toolTarget = {
	drop(props, monitor, flowGraphView) {
		const item = monitor.getItem();
		const center = { ...monitor.getSourceClientOffset() };
		center.x += item.width / 2;
		center.y += item.height / 2;
		const instance = flowGraphView.getWrappedInstance();
		// Focus the graph view after drop so that it can accept keyboard input
		// immediately (e.g. delete).
		instance.graphViewRef.focus();
		if (item.item.category === 'triggers') {
			instance.createTrigger(item.item.triggerType, center);
		} else {
			instance.createNode(item.item, center);
		}
	}
};

function collect(connect, monitor) {
	return {
		connectDropTarget: connect.dropTarget(),
		isOver: monitor.isOver(),
		canDrop: monitor.canDrop()
	};
}

function compareEdges(edge1, edge2) {
	return !!edge1 && !!edge2
		&& edge1.from === edge2.from
		&& edge1.to === edge2.to
		&& edge1.output === edge2.output;
}

class FlowGraphView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.onKeyDown = this.onKeyDown.bind(this);
		this.onWheel = this.onWheel.bind(this);
		this.handleMouseMove = this.handleMouseMove.bind(this);
		this.handleBackgroundMouseDown = this.handleBackgroundMouseDown.bind(this);
		this.handleMouseUp = this.handleMouseUp.bind(this);
		this.onBeginNodeConnect = this.onBeginNodeConnect.bind(this);
		this.onNodeConnect = this.onNodeConnect.bind(this);
		this.onNodePreviewConnect = this.onNodePreviewConnect.bind(this);
		this.getCurrentTransform = this.getCurrentTransform.bind(this);
		this.renderCanvasMessage = this.renderCanvasMessage.bind(this);

		this.nodeRefs = {};
		this.outputHandles = {};

		this.constants = {
			graph: {
				marginx: 50,
				marginy: 200,
				ranksep: 50,
				edgesep: 0
			}
		};

		this.updateTriggersInfo({
			flow: this.props.flow.flow,
			definitions: this.props.triggerDefinitions,
			flowTriggers: this.props.flowTriggers
		});

		this.state = {
			inserting: null,
			nodes: this.renderGraphComponents(props.flow)
		};

		this.localState = {
			scale: 1,
			pan: {
				x: 0,
				y: 0
			},
			mousePosLastUpdate: {
				x: 0,
				y: 0
			},
			updateTimeout: null
		};
	}

	shouldComponentUpdate(nextProps) {
		// filter out drag-and-drop state changes
		if (nextProps.canDrop !== this.props.canDrop) {
			return false;
		} else if (nextProps.isOver !== this.props.isOver) {
			return false;
		}
		return true;
	}

	componentDidMount() {
		// Maybe we just listen on the panel and get react events.
		// Alternatively make sure that all the events work cross-browser.
		window.addEventListener('mousemove', this.handleMouseMove);
		window.addEventListener('mouseup', this.handleMouseUp);

		// Need to let the nodes render so that their sizes can be interrogated.
		this.localState.updateTimeout = setTimeout(() => this.updateGraph(), 0);
	}

	componentWillReceiveProps(nextProps) {
		// Be careful - normally, you update props, you expect the component
		// to render that state.  However, in FlowGraphView, it does not work
		// that way.  There is a dependency on nodes being rendered, which is
		// why `renderGraphComponents` is called from the constructor.  E.g.
		// cannot update the flow, setProps and expect that `updateGraph` is
		// going to work - it won't because the new flow-node has not been
		// added yet.  It _requires_ user interaction.
		this.updateTriggersInfo({
			flow: nextProps.flow.flow,
			definitions: nextProps.triggerDefinitions,
			flowTriggers: nextProps.flowTriggers
		});
	}

	updateTriggersInfo({ flowTriggers, flow, definitions }) {
		// reset internal state
		this.allTriggerIds = [];
		this.triggerIdToNode = {};
		this.endpointIds = {};

		this.updateFlowTriggerIds(flow.triggers, definitions);
		this.updateFlowTriggerBindingIds(flowTriggers);
	}

	updateFlowTriggerIds(triggers, definitions) {
		if (!triggers) {
			return;
		}

		for (const triggerType in triggers) {
			const definition = definitions[triggerType];
			for (const triggerId in triggers[triggerType]) {
				const id = `${TRIGGER_ID_PREFIX}${triggerType}_${triggerId}`;
				this.triggerIdToNode[id] = {
					triggerId,
					triggerType,
					icon: definition && definition.icon,
					trigger: triggers[triggerType][triggerId]
				};
				this.allTriggerIds.push(id);
			}
		}
	}

	updateFlowTriggerBindingIds(flowTriggers) {
		if (!flowTriggers) {
			return;
		}
		for (const bound of flowTriggers.endpoints) {
			const id = `${ENDPOINT_ID_PREFIX}${bound.summary}`;
			this.endpointIds[id] = bound;
			this.allTriggerIds.push(id);
		}
	}

	componentWillUnmount() {
		window.removeEventListener('mousemove', this.handleMouseMove);
		window.removeEventListener('mouseup', this.handleMouseUp);
		if (this.localState.updateTimeout !== null) {
			clearTimeout(this.localState.updateTimeout);
			this.localState.updateTimeout = null;
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.inserting || this.state.inserting) {
			// While inserting avoid re-rendering the graph.
			if (!prevState.inserting && this.state.inserting) {
				// The previous state was not inserting, but the current state is.
				// That means the insert rendered and the new node is currently
				// visible in the location it was dropped. The delay allows for
				// the user to see that node before the graph animates it to
				// the calculated location.
				setTimeout(() => {
					this.setState({ inserting: null });
				}, 50);
			}

			if (prevState.inserting && !this.state.inserting) {
				// The current state is not inserting, but the previous state
				// was inserting, so that means insert is finished.  Callback to
				// indicate that the node was inserted.  The delay will allow
				// things to settle down before the validation kicks in.  It is
				// delayed here to keep these "animation" delays together.
				setTimeout(() => {
					this.props.onNodeInserted(prevState.inserting.nodeId);
				}, 250);
			}
		} else if (this.props !== prevProps) { // Shallow check to just to avoid update cycle
			this.updateGraph();
		}
	}

	updateGraph() {
		const graph = this.buildGraph(this.props.flow);
		if (graph) {
			const nodes = this.renderGraphComponents(graph);
			const edges = this.renderGraphEdges(graph);
			this.setState({
				graph,
				nodes,
				edges
			});
		}
	}

	/**
	 * Creates a new node from a node handler.
	 */
	createNode(nodehandler, center) {
		const node = generateNodeFromNodeHandler(nodehandler);
		const id = this.props.onNodeInsert(node);
		const nodeInfo = {
			selected: true,
			center,
			id,
			style: {
				visibility: 'hidden'
			},
			nodehandler,
			node
		};
		this.renderNode(nodeInfo);

		// Rerenders the graph while doing the node selection
		this.props.onNodeSelect({
			type: 'flow-node',
			id,
			topic: 'parameters'
		});
	}

	/**
	 * Creates a new trigger attached to Start node.
	 */
	createTrigger(triggerType, center) {
		const { triggerDefinitions, newTriggerStatusMsg } = this.props;
		const {
			icon,
			triggerTemplate,
			requestSchema
		} = triggerDefinitions[triggerType];
		const templateCopy = JSON.parse(JSON.stringify(triggerTemplate));
		const triggerId = uuidv4();

		const { parameter = {} } = this.props.flow.flow;
		// Assign flow inputs
		templateCopy.flowInputsMap = buildFlowInputsMap(
			parameter,
			requestSchema
		);

		const newTrigger = {
			...templateCopy,
			flowId: this.props.flow.id,
			status: {
				error: newTriggerStatusMsg,
				valid: true
			}
		};

		// Unfortunately, the trigger definition's template _only_ holds the
		// "default" channel information when it's persisted on disk and
		// _only_ when it is first loaded into the Flow editor.  When we add a
		// new flow-trigger with channel, we need it to become the "default"
		// (even if we Apply the change).  This finds the first flow-trigger in
		// memory and copies the `channelParameters` so that new flow-triggers
		// appear to use use the "single" channel instance.
		for (const id in this.triggerIdToNode) {
			const {
				channelId,
				channelParameters
			} = this.triggerIdToNode[id].trigger;
			if (channelId === newTrigger.channelId) {
				newTrigger.channelParameters = channelParameters;
				break;
			}
		}

		// update the triggers in flow.flow.triggers
		this.props.onTriggerInsert(triggerType, triggerId, newTrigger);
		const id = `${TRIGGER_ID_PREFIX}${triggerType}_${triggerId}`;
		const nodeInfo = {
			center,
			id,
			style: {
				visibility: 'hidden'
			}
		};

		// renderNode/renderGraphComponent requires triggerIdToNode to be
		// updated with the new trigger information so we update it manually
		this.triggerIdToNode[id] = {
			triggerId,
			triggerType,
			icon,
			trigger: newTrigger
		};

		// buildTriggersStartGraph relies on this to be up to date to render
		// triggers.
		this.allTriggerIds.push(id);
		this.renderNode(nodeInfo);

		// Rerenders the graph while doing the trigger selection
		this.props.onNodeSelect({
			type: 'trigger',
			id: id,
			topic: 'parameters',
			options: {
				triggerId,
				type: triggerType
			}
		});
	}

	/**
	 * This method combines different techniques to render a new node with nice
	 * transition on the graph. It uses 'recenter' function, a list with nodes
	 * in memory and a flag called 'inserting' to prevent unnecessary graph
	 * rendering.
	 *
	 * @param {Object} nodeInfo - contains the needed information to render a
	 * new node within the graph
	 */
	renderNode(nodeInfo) {
		// recenter callback is needed to achieve the nice sliding effect when
		// rendering the new trigger on top of the graph
		const { scale } = this.localState;
		const graphRect = this.graphRef.getBoundingClientRect();
		const recenter = (el => {
			// Need to adjust element style after render so that dimensions are correct.
			el.style.top = `${((nodeInfo.center.y - graphRect.top) / scale) - (el.offsetHeight / 2)}px`;
			el.style.left = `${((nodeInfo.center.x - graphRect.left) / scale) - (el.offsetWidth / 2)}px`;
			el.style.visibility = 'visible';
		});
		const inserted = this.renderGraphComponent(
			nodeInfo,
			nodeInfo.nodehandler,
			nodeInfo.selected,
			recenter
		);
		this.setState({
			nodes: [ ...this.state.nodes, inserted ],
			inserting: {
				nodeId: nodeInfo.id
			}
		});
	}

	/**
	 * Zooms in the graph by the specified percentage delta (1 = 100%)
	 */
	zoomIn(scale = 1) {
		// increase the maxGraphScale value if necessary
		const maxGraphScale = 1;
		if (this.localState.scale < maxGraphScale) {
			let graphScale = this.localState.scale * scale;
			if (graphScale > maxGraphScale) {
				graphScale = maxGraphScale;
			}
			this.localState.scale = graphScale;
		}
		this.graphRef.style.transform = this.getCurrentTransform();
		if (this.isConnecting()) {
			this.handleMouseMoveConnecting();
		}
	}

	/**
	 * Zooms out the graph by the specified percentage delta (1 = 100%)
	 */
	zoomOut(scale = 1) {
		const minGraphScale = 0.3;
		if (this.localState.scale > minGraphScale) {
			let graphScale = this.localState.scale / scale;
			if (graphScale < minGraphScale) {
				graphScale = minGraphScale;
			}
			this.localState.scale = graphScale;
		}
		this.graphRef.style.transform = this.getCurrentTransform();
		if (this.isConnecting()) {
			this.handleMouseMoveConnecting();
		}
	}

	/**
	 * Resets the zoom and position of the graph.
	 */
	resetTransform() {
		const { pan } = this.localState;
		pan.x = 0;
		pan.y = 0;
		this.localState.scale = 1;
		this.graphRef.style.transform = this.getCurrentTransform();
		if (this.isConnecting()) {
			this.handleMouseMoveConnecting();
		}
	}

	/**
	 * Pans the graph by the given deltas
	 */
	pan(deltaX = 0, deltaY = 0, handleEdgeRecalculation = true) {
		const { pan } = this.localState;
		// pan relative to the scale
		pan.x += deltaX * (1 / this.localState.scale);
		pan.y += deltaY * (1 / this.localState.scale);
		this.graphRef.style.transform = this.getCurrentTransform();
		if (handleEdgeRecalculation && this.isConnecting()) {
			this.handleMouseMoveConnecting();
		}
	}

	getCurrentTransform() {
		const { pan, scale } = this.localState;
		return `scale(${scale}) translate(${pan.x}px, ${pan.y}px)`;
	}

	onKeyDown(e) {
		e.stopPropagation();
		e.preventDefault();

		// React 17 adds support for e.code so for now we get it from the native event
		const { code } = e.nativeEvent;

		if (code === 'Delete' || code === 'Backspace') {
			const { selectedNode, selectedEdge } = this.props;

			if (selectedNode
					&& selectedNode.id !== START_NODE_ID
					&& selectedNode.type !== 'endpoint') {
				this.props.onNodeDelete();
			} else if (selectedEdge) {
				if (selectedEdge.from !== START_NODE_ID) {
					this.props.onEdgeDelete();
				} else {
					this.props.onSetStartNode(null);
				}
			}
		}

		if (code === 'Digit0' || code === 'Numpad0') {
			this.resetTransform();
		}

		// 'code' is used for the physical keyboard location. Some layouts like Dvorak have certain
		// keys in different places. 'Minus' and 'Equal' will match the keys to the right of 0.
		// We really want to test any press of the key which has the symbol printed on it, but there
		// isn't an easy way of doing that. This works for most people and other products have
		// similar limitations.
		if (code === 'Minus' || code === 'NumpadSubtract') {
			this.zoomOut(1.1);
		}

		if (code === 'Equal' || code === 'NumpadAdd') {
			this.zoomIn(1.1);
		}

		if (code === 'ArrowUp') {
			this.pan(0, 10);
		}

		if (code === 'ArrowDown') {
			this.pan(0, -10);
		}

		if (code === 'ArrowLeft') {
			this.pan(10, 0);
		}

		if (code === 'ArrowRight') {
			this.pan(-10, 0);
		}

		if (code === 'Escape') {
			if (this.isConnecting()) {
				this.setState({
					connecting: null
				},
				() => this.updateGraph());
			}
		}
	}

	onWheel(e) {
		if (e.deltaY < 0) {
			this.zoomIn(1.1);
		} else if (e.deltaY > 0) {
			this.zoomOut(1.1);
		}
	}

	handleBackgroundMouseDown(e) {
		const backgroundIsTarget = e.target === e.currentTarget;
		if (this.isPanning() || !backgroundIsTarget) {
			return;
		}
		this.localState.mousePosLastUpdate = {
			x: e.clientX,
			y: e.clientY
		};

		if (!this.isPanning()) {
			this.setState({
				panning: true
			});
		}
	}

	handleMouseUp() {
		const state = {};

		if (this.isPanning()) {
			state.panning = false;
		}

		if (this.isConnecting()) {
			state.connecting = null;
		}

		if (Object.keys(state).length > 0) {
			this.setState(state, () => this.updateGraph());
		}
	}

	handleMouseMove(e) {
		this.handleMouseMovePan(e);
		this.handleMouseMoveConnecting(e);
	}

	handleMouseMoveConnecting(e) {
		if (!this.isConnecting()) {
			return;
		}
		if (e) {
			e.preventDefault();
			e.stopPropagation();
			this.localState.mousePosLastUpdate = {
				x: e.clientX,
				y: e.clientY
			};
		}

		const mousePos = this.localState.mousePosLastUpdate;

		let end;
		const connecting = this.state.connecting;
		const graphViewRect = this.graphViewRef.getBoundingClientRect();
		const graphRect = this.graphRef.getBoundingClientRect();
		const { scale } = this.localState;

		// Convert a point inside the transformed graph ref to screen coordinates.
		const graphPointToContainerSpace = ({ x, y }) => {
			return {
				x: (x * scale) + graphRect.left - graphViewRect.left,
				y: (y * scale) + graphRect.top - graphViewRect.top
			};
		};

		const relativeMousePos = {
			x: (mousePos.x - graphViewRect.left),
			y: (mousePos.y - graphViewRect.top)
		};

		if (connecting.currentTarget) {
			// Snap to targets top.
			end = graphPointToContainerSpace(
				this.state.graph.nodes[connecting.currentTarget].controlPoints[TOP]
			);
		} else {
			// End of line is mouse pointer (relative to the graph background).
			end = relativeMousePos;
		}

		// if dragging outside the bounds of the graph then pan in that direction.
		let panX = 0;
		let panY = 0;
		const dragSpeed = DRAG_PAN_SPEED * this.localState.scale;
		if (relativeMousePos.x < 0) {
			panX = dragSpeed;
		} else if (relativeMousePos.x > graphViewRect.width) {
			panX = -dragSpeed;
		}
		if (relativeMousePos.y < 0) {
			panY = dragSpeed;
		} else if (relativeMousePos.y > graphViewRect.height) {
			panY = -dragSpeed;
		}

		if (panX !== 0 || panY !== 0) {
			// Pan the graph but ensure we don't recursively call this function again
			this.pan(panX, panY, false);
		}

		const { sourceNode, sourceOutput } = connecting;
		const start = graphPointToContainerSpace(
			this.state.graph.nodes[sourceNode].controlPoints[sourceOutput]
		);

		this.setState({
			connecting: {
				...this.state.connecting,
				edge: (
					<FlowGraphEdge
						id={`${sourceNode}:${sourceOutput}`}
						className={
							`connecting ${this.state.connecting.currentTarget ? 'preview' : ''}`
						}
						key={`connecting${sourceNode}${sourceOutput}group`}
						points={[ start, end ]}
					/>
				)
			}
		});
	}

	handleMouseMovePan(e) {
		if (!this.isPanning()) {
			return;
		}
		const deltaX = this.localState.mousePosLastUpdate.x - e.clientX;
		const deltaY = this.localState.mousePosLastUpdate.y - e.clientY;
		this.localState.mousePosLastUpdate = {
			x: e.clientX,
			y: e.clientY
		};
		this.pan(-deltaX, -deltaY);
	}

	// Get the dimensions of the node - can only be called after nodes have been rendered.
	getNodeDimensions(vertex) {
		const dim = { };
		if (this.nodeRefs[vertex.id]) {
			dim.w = this.nodeRefs[vertex.id].offsetWidth;
			dim.h = this.nodeRefs[vertex.id].offsetHeight;
		}
		return dim;
	}

	/**
	 * Given a set of `vertices` and `edges`, this will calculate the positions
	 * of each.  A Vertex is: `{ id, parents, children, className, info }`.
	 * An Edge is: `{ from, to, output, className }`
	 * @param {object[]} vertices - An array of vertices.
	 * @param {object[]} edges - An array of edges.
	 * @param {options} options - Graph options.
	 * @param {number} [options.offsetX=0] - The number of pixels to shift
	 * the graph on the x-axis.
	 * @param {number} [options.offsetY=0] - The number of pixels to shift
	 * the graph on the x-axis.
	 * @return A graph { nodes, edges, width, height }
	 */
	calculateGraph(vertices, edges, options) {
		// init dagre graph
		const g = new graphlib.Graph({ multigraph: true });
		g.setGraph(this.constants.graph);
		g.setDefaultEdgeLabel(() => ({}));

		const {
			offsetX, // linting thing, always supplied
			offsetY = 0,
			injectPredecessor
		} = options;

		// add vertices to dagre graph
		vertices.forEach(vertex => {
			const dim = this.getNodeDimensions(vertex);
			g.setNode(vertex.id, {
				...vertex.info,
				width: dim.w,
				height: dim.h
			});
		});

		// add edges to dagre graph
		edges.forEach(edge => {
			g.setEdge(
				edge.from,
				edge.to,
				{
					...edge,
					minlen: 0
				},
				`${edge.to}${edge.from}${edge.output}`
			);
		});

		// generate layout
		layout(g);

		const graph = {
			nodes: {},
			edges: [],
			width: g.graph().width,
			height: g.graph().height
		};

		const ancestors = (n) => {
			const allAncestors = new Set();
			if (injectPredecessor) {
				allAncestors.add(injectPredecessor);
			}

			const walk = (node) => {
				g.predecessors(node).forEach(p => {
					if (!allAncestors.has(p)) {
						allAncestors.add(p);
						walk(p);
					}
				});
			};

			walk(n);
			return Array.from(allAncestors);
		};

		// add nodes to final graph
		g.nodes().forEach(n => {
			const node = graph.nodes[n] = g.node(n);
			node.x += offsetX;
			node.y += offsetY;
			graph.nodes[n] = {
				...node,
				controlPoints: {
					[TOP]: {
						x: node.x,
						y: node.y - (node.height / 2)
					}
				},
				predecessors: ancestors(n)
			};

			// Add output handle control points.
			Object.keys(this.outputHandles[n]).forEach(output => {
				const rect = this.outputHandles[n][output];

				graph.nodes[n].controlPoints[output] = {
					x: node.x - (node.width / 2) + rect.x,
					y: node.y - (node.height / 2) + rect.y
				};
			});
		});

		graph.edges = g.edges().map(e => {
			// dagre generates edges to dodge nodes
			// and other edges so we want to keep the basic shape.
			// This changes the start and end points and adds right angles
			const edge = g.edge(e);
			const points = edge.points;
			const end = graph.nodes[edge.to].controlPoints[TOP];

			if (graph.nodes[edge.from].controlPoints.hasOwnProperty(edge.output)) {
				points[0] = graph.nodes[edge.from].controlPoints[edge.output];
			}

			if (end) {
				points[points.length - 1] = end;
			}

			// The edges have at least 2 points(from output to start of
			// next node). If the edge requires to go around nodes it could
			// have multiple additional points. We need to apply the offset
			// to all these snap points.
			if (points.length > 2) {
				// Everything in between the output point(first) and the
				// start node point(last).
				for (let i = 1; i < points.length - 1; ++i) {
					points[i].x += offsetX;
				}
			}

			return edge;
		});

		return graph;
	}

	/**
	 * Builds a graph from the trigger node(s) and Start node.
	 * @param {object} flow - The flow.
	 * @param {number} [offsetX=0] - The number of pixels to shift the graph
	 * on the x-axis.
	 * @return A graph { nodes, edges, width, height }
	 */
	buildTriggersStartGraph(flow, offsetX = 0) {
		const vertices = [];
		const edges = [];

		// Add triggers
		for (const id of this.allTriggerIds) {
			vertices.unshift({ id });
			edges.push({
				from: id,
				to: START_NODE_ID,
				output: 'start',
				className: 'trigger-edge'
			});
		}

		// Add the start node
		vertices.unshift({
			id: START_NODE_ID,
			children: {},
			className: 'start'
		});

		const offsetY = edges.length ? -140 : -82;
		return this.calculateGraph(vertices, edges, { offsetX, offsetY });
	}

	/**
	 * Builds a graph from the flow node(s).
	 * @param {object} flow - The flow.
	 * @param {number} offsetX - The number of pixels to shift the graph on
	 * the x-axis.
	 * @return A graph { nodes, edges, width, height }
	 */
	buildFlowGraph(flow, offsetX = 0) {
		const vertices = [ ...flow.getVertices() ];
		const edges = [ ...flow.getEdges() ];
		// Add the start node
		const options = { offsetX };
		if (flow.flow.start) {
			options.injectPredecessor = START_NODE_ID;
		}

		return this.calculateGraph(vertices, edges, options);
	}

	buildGraph(flow) {
		let offsetX = 0;
		let flowGraph = this.buildFlowGraph(flow);
		if (flow.flow.start) {
			// If there is a flow.start node AND there are none or 1 trigger,
			// then the Start graph needs to be shifted by the x-axis center
			// position to align with the flow.start node.
			if (this.allTriggerIds.length <= 1) {
				offsetX = flowGraph.nodes[flow.flow.start].x - 117.5;
			}
		} else if (Number.isFinite(flowGraph.width)) {
			// The start should be aligned with the center of the disconnected
			// graph.  If the graph has not yet rendered, the width/height is
			// -Infinity.
			offsetX = flowGraph.width / 2 - 117.5;
		}
		const startGraph = this.buildTriggersStartGraph(flow, offsetX);

		if (flow.flow.start && startGraph.nodes[START_NODE_ID]
			&& startGraph.nodes[START_NODE_ID].x
			!== flowGraph.nodes[flow.flow.start].x) {
			// If the Start and flow.start do not align, re-align by
			// calculating the x-axis offset and rebuilding the graph
			offsetX = startGraph.nodes[START_NODE_ID].x
				- flowGraph.nodes[flow.flow.start].x;
			flowGraph = this.buildFlowGraph(flow, offsetX);
		}

		// merge the two graphs
		const graph = {
			nodes: {
				...flowGraph.nodes,
				...startGraph.nodes
			},
			edges: [
				...flowGraph.edges,
				...startGraph.edges
			],
			// JAMIE: this probably needs to improve
			width: Math.max(flowGraph.width, startGraph.width),
			height: Math.max(flowGraph.height, startGraph.height)
		};

		// Link the predecessor from the graph start to the start node
		if (flow.flow.start && graph.nodes[START_NODE_ID]) {
			const flowStart = graph.nodes[flow.flow.start];

			// The `replacing` class is only applied to the start node and is
			// an effect that causes the existing line to disappear when
			// dragging from an existing connection to a new connection.
			// There cannot be two connections from Start.  If that were to
			// change, then this should be removed.
			const replacing = this.state.connecting
				&& this.state.connecting.sourceNode === START_NODE_ID;

			// Add an edge from the flow graph start to the start node
			const start = graph.nodes[START_NODE_ID].controlPoints.start;
			graph.edges.push({
				className: cn('start', { replacing }),
				from: START_NODE_ID,
				to: flow.flow.start,
				minlen: 0,
				output: 'start',
				points: [
					start,
					flowStart.controlPoints[TOP]
				]
			});
		}
		return graph;
	}

	onBeginNodeConnect(sourceNode, sourceOutput) {
		const { graph } = this.state;

		// Determine the list of potential targets - not self, not start, not predecessor
		// and not already connected.
		const source = graph.nodes[sourceNode];
		const potentialTargets = Object.keys(graph.nodes).filter(n =>
			n !== sourceNode
			&& ![ START_NODE_ID, ...this.allTriggerIds ].includes(n)
			&& source.predecessors.indexOf(n) === -1
			&& !graph.edges.find(
				e => e.output === sourceOutput && e.from === sourceNode && e.to === n)
		);

		this.setState(
			{
				graph,
				edgeHovered: null,
				connecting: {
					sourceNode,
					sourceOutput,
					potentialTargets
				}
			},
			() => this.updateGraph()
		);
	}

	onNodePreviewConnect(target, selected) {
		if (this.isConnecting()) {
			if (selected) {
				this.state.connecting.currentTarget = target;
			} else {
				this.state.connecting.currentTarget = null;
			}
			this.setState({
				connecting: this.state.connecting
			});
		}
	}

	onNodeConnect(target) {
		if (this.isConnecting()) {
			const { sourceNode, sourceOutput } = this.state.connecting;
			if (sourceNode === START_NODE_ID) {
				this.props.onSetStartNode(target);
			} else {
				this.props.onNodeConnect(sourceNode, sourceOutput, target);
			}
			this.setState({
				connecting: null
			}, () => this.updateGraph());
		}
	}

	renderGraphComponents(flow) {
		const { nodehandlers, selectedNode } = this.props;
		const flowNodes = this.props.flow.flow.nodes;
		let renderNodes = [];

		if (flow.getVertices) {
			// It's a raw flow
			renderNodes = flow.getVertices().map(vertex => ({
				id: vertex.id,
				node: flowNodes[vertex.id],
				style: { }
			}));

			for (const id of this.allTriggerIds) {
				renderNodes.push({ id });
			}
			renderNodes.push({ id: START_NODE_ID });
		} else if (flow.nodes) {
			// It's a layed out graph
			renderNodes = Object.keys(flow.nodes).map(id => ({
				id,
				node: flowNodes[id],
				style: {
					left: flow.nodes[id].x - (flow.nodes[id].width / 2),
					top: flow.nodes[id].y - (flow.nodes[id].height / 2)
				}
			}));
		}

		return renderNodes.map(renderNode => {
			const id = renderNode.id;
			const node = renderNode.node;
			const nodehandler = node && node.type && nodehandlers[node.type];
			const selected = selectedNode && (selectedNode.id === id);
			return this.renderGraphComponent(renderNode, nodehandler, selected);
		});
	}

	renderGraphComponent(renderNode, nodehandler, selected, postRender) {
		const { validationMap, triggered } = this.props;
		const id = renderNode.id;
		const node = renderNode.node;
		const style = renderNode.style;
		const connecting = this.state && this.state.connecting;
		const nodeErrors = validationMap && validationMap.nodeErrors
			&& validationMap.nodeErrors[id];
		const showStartWarning = !triggered && !nodeErrors;

		let nodeComp;
		if (id === START_NODE_ID) {
			nodeComp = (
				<FlowGraphStartNode
					id={id}
					// we want to supress selected look in case not triggered
					selected={selected}
					className={showStartWarning ? 'warning' : ''}
					outputHandles={(rects) => { this.outputHandles[id] = rects; }}
					onSelect={() => this.props.onNodeSelect({
						id,
						type: 'start',
						topic: 'parameters'
					})}
					connecting={connecting && {
						source: id === connecting.sourceNode,
						output: id === connecting.sourceNode ? connecting.sourceOutput : null
					}}
					onStartConnect={this.onBeginNodeConnect}
					invalid={!!nodeErrors}
				/>
			);
		} else if (this.triggerIdToNode.hasOwnProperty(id)) {
			const disabled = connecting && (
				connecting.sourceNode !== id
				|| !connecting.potentialTargets.includes(id)
			);
			const {
				triggerId,
				triggerType,
				trigger,
				icon
			} = this.triggerIdToNode[id];
			const triggerState = {
				invalid: !trigger.status.valid || !!nodeErrors,
				disabled: !trigger.enabled
			};

			nodeComp = (
				<FlowGraphTriggerNode
					id={id}
					name={trigger.instanceName || triggerId}
					icon={icon}
					outputHandles={(rects) => { this.outputHandles[id] = rects; }}
					selected={selected}
					disabled={disabled}
					className={cn('trigger', triggerState)}
					onSelect={() => {
						return this.props.onNodeSelect({
							type: 'trigger',
							id,
							topic: 'parameters',
							options: {
								type: triggerType,
								triggerId
							}
						});
					}}
				/>
			);
		} else if (this.endpointIds.hasOwnProperty(id)) {
			const disabled = connecting && (
				connecting.sourceNode !== id
				|| !connecting.potentialTargets.includes(id)
			);
			const trigger = this.endpointIds[id];
			const triggerState = {
				invalid: !trigger.status.valid,
				disabled: !trigger.enabled
			};
			nodeComp = (
				<FlowGraphTriggerNode
					id={id}
					name={trigger.summary}
					icon="icon-GG-api"
					outputHandles={(rects) => { this.outputHandles[id] = rects; }}
					selected={selected}
					triggered
					disabled={disabled}
					className={cn('trigger', triggerState)}
					onSelect={() => {
						return this.props.onNodeSelect({
							type: 'endpoint',
							id,
							topic: 'parameters',
							options: {
								summary: trigger.summary
							}
						});
					}}
				/>
			);
		} else {
			nodeComp = (
				<FlowGraphNode
					id={id}
					node={node}
					nodehandler={nodehandler
						|| { type: node.type, name: node.type, icon: node.icon }}
					selected={selected}
					outputHandles={(rects) => { this.outputHandles[id] = rects; }}
					invalid={!!nodeErrors}
					invalidOutput={nodeErrors && nodeErrors.outputsInvalid}
					onSelect={(id, { type, output }) => this.props.onNodeSelect({
						type: 'flow-node',
						id,
						topic: type, // parameters|outputs
						options: output
					})}
					onTargetSelectable={(n, selectable) => this.onNodePreviewConnect(n, selectable)}
					onTargetSelect={() => this.onNodeConnect(id)}
					onStartConnect={this.onBeginNodeConnect}
					connecting={connecting && {
						source: id === connecting.sourceNode,
						output: id === connecting.sourceNode ? connecting.sourceOutput : null,
						targetable: connecting.potentialTargets.indexOf(id) !== -1
					}}
				/>
			);
		}
		return (
			<div
				className="node-container"
				key={id}
				ref={(r) => {
					this.nodeRefs[id] = r;
					r && postRender && postRender(r);
				}}
				style={style}
			>
				{nodeComp}
			</div>
		);
	}

	renderGraphEdges(graphData) {
		const { selectedEdge, validationMap } = this.props;
		const edges = Array.from(graphData.edges);
		const connecting = this.isConnecting();

		if (!connecting) {
			// if an edge is selected, then need to push it to be the last edge so that it will
			// not be clipped.
			const index = edges.findIndex(edge => compareEdges(selectedEdge, edge));
			if (index >= 0 && index !== edges.length - 1) {
				const selected = edges.splice(index, 1);
				edges.push(selected[0]);
			}
		}
		// The edge has 2 paths, one is viewable, but the other is transparant and used for
		// hover/selection (hover-target).  hover-target is wide to make it easier to select and
		// the select, and hover events are propaged to be managed by this component.
		return edges.map((edge) => {
			// get the validation for the node that the edge originated at
			const invalidOutput = validationMap
				&& validationMap.nodeErrors
				&& validationMap.nodeErrors[edge.from]
				&& validationMap.nodeErrors[edge.from].outputsInvalid
				&& validationMap.nodeErrors[edge.from].outputsInvalid[edge.output];

			const isTriggerEdge = edge.to === START_NODE_ID;
			const selectable = !connecting && !isTriggerEdge;

			// Render edge as invalid if the output it's coming from is unknown or invalid selector
			const unknownSource = invalidOutput === 'NOT_IN_SPEC'
				|| invalidOutput === 'INVALID_JSONPATH';
			const selected = selectable && compareEdges(selectedEdge, edge);
			const hovered = selectable && !!this.state.edgeHovered
				&& compareEdges(this.state.edgeHovered, edge);
			return (
				<FlowGraphEdge
					key={`${edge.from}:${edge.output}:${edge.to}`}
					id={`${edge.from}:${edge.output}:${edge.to}`}
					className={edge.className}
					selectable={selectable}
					selected={selected}
					hovered={hovered}
					points={edge.points}
					onSelect={() => this.props.onEdgeSelect(edge)}
					onMouseEnter={() => {
						this.setState({ edgeHovered: edge }, () => this.updateGraph());
					}}
					onMouseLeave={() => {
						this.setState({ edgeHovered: null }, () => this.updateGraph());
					}}
					invalid={unknownSource}
				/>
			);
		});
	}

	isConnecting() {
		return this.state && !!this.state.connecting;
	}

	isPanning() {
		return this.state && !!this.state.panning;
	}

	renderCanvasMessage() {
		const { validationMap, flow } = this.props;
		const { graph } = this.state;
		if (!graph) {
			// don't render a message if the graph hasn't been calculated yet.
			return;
		}
		const validation = (validationMap || {}).nodeErrors || {};
		const hasErrors = Object.keys(validation).length;
		let message;
		let style;
		if (!flow.getNodes().length) {
			message = this.intl.formatMsg('To start, drag and drop a flow-node from the panel on the left onto the canvas below.');
		} else if (validation.$$__axway__start) {
			style = 'danger';
			message = this.intl.formatMsg('This flow contains error(s). Start must be connected to another flow-node.');
		} else if (hasErrors) {
			style = 'danger';
			message = this.intl.formatMsg('This flow contains error(s). Please check the highlighted flow-nodes and their configuration.');
		} else {
			return;
		}
		return (
			<Alert bsStyle={style}>
				{message}
			</Alert>
		);
	}

	render() {
		const { connectDropTarget, selectedEdge } = this.props;
		const graph = this.state.graph;

		let classModifiers = '';
		if (this.isConnecting()) {
			classModifiers = `${classModifiers} connecting`;
		}
		if (this.isPanning()) {
			classModifiers = `${classModifiers} panning`;
		}

		return connectDropTarget(
			<div
				tabIndex={0}
				ref={g => { this.graphViewRef = g; }}
				className={`flow-graph-background ${classModifiers}`}
				onMouseMove={this.handleMouseMove}
				onMouseDown={this.handleBackgroundMouseDown}
				onKeyDown={this.onKeyDown}
				onWheel={this.onWheel}
			>
				{!graph && (
					<div className="flow-graph" style={{ visibility: 'hidden' }}>
						<div className="graph-nodes">
							{this.state.nodes}
						</div>
					</div>
				)}
				{graph && (
					<div
						className={`flow-graph ${(this.isConnecting()) ? 'connecting' : ''}`}
						ref={g => { this.graphRef = g; }}
						style={{
							left: '50%',
							marginLeft: `-${graph.width / 2}px`,
							width: graph.width,
							height: graph.height,
							transform: this.getCurrentTransform()
						}}
					>
						<div className="graph-nodes">
							{this.state.nodes}
						</div>
						<svg className={`graph-edges${selectedEdge ? ' selected' : ''}`}>
							{this.state.edges}
						</svg>
					</div>
				)}
				{this.renderCanvasMessage()}
				<svg className="connecting graph-edges">
					{this.isConnecting() && this.state.connecting.edge}
				</svg>
			</div>
		);
	}
}

FlowGraphView.propTypes = {
	/**
	 * The flow to render
	 */
	flow: PropTypes.object.isRequired,
	/**
	 * The bindings to the flow
	 */
	flowTriggers: FlowPropTypes.flowTriggers,
	/**
	 * The flow-trigger plugin definitions.
	 */
	triggerDefinitions: FlowPropTypes.triggerDefinitions,
	/**
	 * The status message for newly created triggers.
	 */
	newTriggerStatusMsg: PropTypes.string,
	/**
	 * The node handlers
	 */
	nodehandlers: PropTypes.object.isRequired,
	/**
	 * Callback when a node is selected. Callback is passed the id of the node clicked.
	 */
	onNodeSelect: PropTypes.func.isRequired,
	/**
	 * Callback when a node is connected as the start node. Callback is passed the id of the
	 * node.
	 */
	onSetStartNode: PropTypes.func.isRequired,
	/**
	 * Callback when a node is connected to another node. Callback is passed the id of the
	 * source node, the source output and the target node.
	 */
	onNodeConnect: PropTypes.func.isRequired,
	/**
	 * Callback when a node is marked for deletion.
	 */
	onNodeDelete: PropTypes.func.isRequired,
	/**
	 * Callback when a node is insert.
	 */
	onNodeInsert: PropTypes.func.isRequired,
	/**
	 * Callback when a node is done inserting.
	 */
	onNodeInserted: PropTypes.func.isRequired,
	/**
	 * Callback when an edge is selected.
	 */
	onEdgeSelect: PropTypes.func.isRequired,
	/**
	 * Callback when an edge is marked for deletion.
	 */
	onEdgeDelete: PropTypes.func.isRequired,
	/**
	 * The selected node.
	 */
	selectedNode: FlowPropTypes.selectedNode,
	/**
	 *
	 */
	validationMap: PropTypes.shape({
		valid: PropTypes.bool,
		nodeErrors: PropTypes.object
	})
};

// Wrap the make a drop target
const FlowGraphViewIntl = injectIntl(FlowGraphView, { withRef: true });
export default DropTarget('tool', toolTarget, collect)(FlowGraphViewIntl);
